import { SignUpMethod } from '@/helpers/westeros';
import {
  Context,
  EvaluationStatus,
  AdmissionType as StateAdmissionTypeKey,
} from '@/pipeline/stateMachine';
import gql from 'graphql-tag';
import { AnyEventObject, StateConfig } from 'xstate';

interface AllCities {
  city: string;
  state: string;
}

export interface AllCitiesQueryResult {
  allCities: AllCities[];
}

interface AllCampus {
  id: number;
  name: string;
  neighborhood: string;
  address: string;
}
export interface AllCampusQueryResult {
  allCampus: AllCampus[];
}

interface AllCourses {
  id: number;
  name: string;
  courseType: string;
  durationYears: number;
}

export interface AllCoursesQueryResult {
  allCourses: AllCourses[];
}

interface CampusInfo {
  id: number;
  name: string;
  address: string;
  neighborhood: string;
  description: string;
  campusPhotos: { photoUrl: string }[];
  campusAmenities: {
    title?: string;
    description?: string;
    photoUrl?: string;
    amenityType?: string;
  }[];
}

export interface CampusInfoQueryResult {
  campusInfo: CampusInfo;
}

interface AllAdmissionTypes {
  id: number;
  name: string;
  description: string;
  evaluationMethod: string;
}

export interface AllAdmissionTypesQueryResult {
  allAdmissionTypes: AllAdmissionTypes[];
}

interface GetUser {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  signupMethod: SignUpMethod;
}

export interface GetUserQueryResult {
  getUser: GetUser;
}

interface GetSettings {
  key: string;
  value: string | number | any[] | object;
}

export interface GetSettingsQueryResult {
  getSettings: GetSettings;
}

interface AllInstitutions {
  id: number;
  name: string;
}

export interface AllInstitutionsQueryResult {
  allInstitutions: AllInstitutions[];
}

interface AllEssayThemes {
  id: number;
  title: string;
  description: string;
}

export interface AllEssayThemesQueryResult {
  allEssayThemes: AllEssayThemes[];
}

interface AllEssaySupportTexts {
  id: number;
  title: string;
  text: string;
  position: number;
}

export interface AllEssaySupportTextsQueryResult {
  allEssaySupportTexts: AllEssaySupportTexts[];
}

export interface GetAdmission {
  id: number;
  evaluationStatus: EvaluationStatus;
  user: { id: number; fullName: string; email: string; phone: string; signupMethod: string };
  course: { id: number; isAvailable: boolean };
  campus: { id: number; name: string; city: string; state: string; isAvailable: boolean };
  admissionType: {
    id: number;
    name: string;
    description: string;
    evaluationMethod: StateAdmissionTypeKey;
  };
  evaluationEssay: {
    id: number;
    essayTheme: { id: number; title: string; description: string };
    grade: number;
    userEssayTitle: string;
    userEssayText: string;
    documentUrl: string;
    hasAcceptedToS: boolean;
  };
  evaluationEnem: {
    id: number;
    year: number;
    inscription: string;
    languagesGrade: number;
    humanScienceGrade: number;
    natureScienceGrade: number;
    mathGrade: number;
    essayGrade: number;
    hasAcceptedToS: boolean;
  };
  evaluationSecondGraduation: {
    id: number;
    institution: string;
    courseName: string;
    year: string;
    documentUrl: string;
    documentBackUrl: string;
    hasAcceptedToS: boolean;
  };
  evaluationTransfer: {
    id: number;
    institution: string;
    courseName: string;
    period: string;
    documentUrl: string;
    hasAcceptedToS: boolean;
  };
}

export interface GetAdmissionQueryResult {
  getAdmission: GetAdmission;
}

interface GetPipelineRun {
  id: number;
  state: StateConfig<Context, AnyEventObject>;
}

export interface GetPipelineRunQueryResult {
  getPipelineRun: GetPipelineRun;
}

export const allCities = gql`
  query allCities($take: Float, $skip: Float, $isAvailable: Boolean) {
    allCities(take: $take, skip: $skip, isAvailable: $isAvailable) {
      city
      state
    }
  }
`;

export const allCampus = gql`
  query allCampus(
    $city: String
    $state: String
    $take: Float
    $skip: Float
    $isAvailable: Boolean
  ) {
    allCampus(city: $city, state: $state, take: $take, skip: $skip, isAvailable: $isAvailable) {
      id
      name
      neighborhood
      address
    }
  }
`;

export const allCourses = gql`
  query allCourses($take: Float, $skip: Float, $isAvailable: Boolean) {
    allCourses(take: $take, skip: $skip, isAvailable: $isAvailable) {
      id
      name
      courseType
      durationYears
    }
  }
`;

export const campusInfo = gql`
  query campusInfo($id: Float!) {
    campusInfo(id: $id) {
      id
      name
      address
      neighborhood
      description
      campusPhotos {
        photoUrl
      }
      campusAmenities {
        title
        description
        photoUrl
        amenityType
      }
    }
  }
`;

export const allAdmissionTypes = gql`
  query allAdmissionTypes($take: Float, $skip: Float) {
    allAdmissionTypes(take: $take, skip: $skip) {
      id
      name
      description
      evaluationMethod
    }
  }
`;

export const getUser = gql`
  query getUser($email: String, $phone: String) {
    getUser(email: $email, phone: $phone) {
      id
      fullName
      email
      phone
      signupMethod
    }
  }
`;

export const getSettings = gql`
  query getSettings($key: String!) {
    getSettings(key: $key) {
      key
      value
    }
  }
`;

export const allInstitutions = gql`
  query allInstitutions($nameContains: String!, $take: Float, $skip: Float) {
    allInstitutions(nameContains: $nameContains, take: $take, skip: $skip) {
      id
      name
    }
  }
`;

export const allEssayThemes = gql`
  query allEssayThemes($take: Float, $skip: Float, $isAvailable: Boolean) {
    allEssayThemes(take: $take, skip: $skip, isAvailable: $isAvailable) {
      id
      title
      description
    }
  }
`;

export const allEssaySupportTexts = gql`
  query allEssaySupportTexts($essayThemeId: Float, $take: Float, $skip: Float) {
    allEssaySupportTexts(essayThemeId: $essayThemeId, take: $take, skip: $skip) {
      id
      title
      text
      position
    }
  }
`;

export const admissionsResultFields = `
  id
  evaluationStatus
  user {
    id
    fullName
    email
    phone
    signupMethod
  }
  course {
    id
    isAvailable
  }
  campus {
    id
    name
    city
    state
    isAvailable
  }
  admissionType {
    id
    name
    description
    evaluationMethod
  }
  evaluationEssay {
    id
    essayTheme {
      id
      title
      description
    }
    grade
    userEssayTitle
    userEssayText
    documentUrl
    hasAcceptedToS
  }
  evaluationEnem {
    id
    year
    inscription
    languagesGrade
    humanScienceGrade
    natureScienceGrade
    mathGrade
    essayGrade
    hasAcceptedToS
  }
  evaluationSecondGraduation {
    id
    institution
    courseName
    year
    documentUrl
    documentBackUrl
    hasAcceptedToS
  }
  evaluationTransfer {
    id
    institution
    courseName
    period
    documentUrl
    hasAcceptedToS
  }
`;

export const getAdmission = gql`
  query getAdmission {
    getAdmission {
      ${admissionsResultFields}
    }
  }
`;

export const getPipelineRun = gql`
  query getPipelineRun($admissionId: Float!) {
    getPipelineRun(admissionId: $admissionId) {
      id
      state
    }
  }
`;
